import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useMemo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';
import LoadingButton from '@shared/components/LoadingButton';
import { compensationsService } from '@modules/staff/person/forms/employeePaidLeave/services/compensations';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

/**
 * @param {'employee'|'employer'} variant — employee: first table; employer: second table (+ year, sum).
 * @param {number|null|undefined} contractId — contract scope for list GET + create POST body.
 */
export default function CompensationsForm({ onClose, variant, contractId, onSuccess }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [loading, setLoading] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [dropdownOptions, setDropdownOptions] = useState([]);

    const validationSchema = useMemo(() => {
        const base = {
            compensationCombo: yup.string().required(t('common.required')),
            count: yup
                .number()
                .typeError(t('common.required'))
                .required(t('common.required'))
                .min(0),
        };
        if (variant === 'employer') {
            return yup.object().shape({
                ...base,
                forYear: yup.number().typeError(t('common.required')).required(t('common.required')),
                sumForDelivery: yup.number().typeError(t('common.required')).required(t('common.required')),
            });
        }
        return yup.object().shape(base);
    }, [variant, t]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            compensationCombo: '',
            count: '',
            forYear: '',
            sumForDelivery: '',
        },
    });

    useEffect(() => {
        reset({
            compensationCombo: '',
            count: '',
            forYear: '',
            sumForDelivery: '',
        });
    }, [variant, contractId, reset]);

    useEffect(() => {
        if (contractId == null) {
            setDropdownOptions([]);
            setOptionsLoading(false);
            return;
        }
        let cancelled = false;
        setOptionsLoading(true);
        setDropdownOptions([]);
        const fetchList =
            variant === 'employee'
                ? compensationsService.getCompensationFromEmpList(contractId)
                : compensationsService.getCompensationFromEmplrList(contractId);
        fetchList
            .then((data) => {
                if (!cancelled) setDropdownOptions(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                console.error('CompensationsForm dropdown list:', e);
                if (!cancelled) {
                    setDropdownOptions([]);
                    notifyError(e?.response?.data?.message || e?.message || t('common.errorMessage'));
                }
            })
            .finally(() => {
                if (!cancelled) setOptionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [variant, contractId, t]);

    const submitForm = async (data) => {
        if (contractId == null || !Number.isFinite(Number(contractId))) {
            notifyError(t('persons.compensations.form.missingContractId'));
            return;
        }
        const compensationId = Number(data.compensationCombo);
        if (!Number.isFinite(compensationId)) {
            notifyError(t('common.required'));
            return;
        }
        const amount = Number(data.count);
        if (!Number.isFinite(amount)) {
            notifyError(t('common.required'));
            return;
        }
        setLoading(true);
        try {
            if (variant === 'employee') {
                await compensationsService.createEmployeeCompensation({
                    contractId: Number(contractId),
                    compensationId,
                    amount,
                });
            } else {
                await compensationsService.createEmployerCompensation({
                    contractId: Number(contractId),
                    compensationId,
                    amount,
                    year: Number(data.forYear),
                    totalAmount: Number(data.sumForDelivery),
                });
            }
            notifySuccess(t('common.createdSuccessfully'));
            onSuccess?.();
            onClose();
        } catch (e) {
            console.error('CompensationsForm submit:', e);
            notifyError(e?.response?.data?.message || e?.message || t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    const title =
        variant === 'employee'
            ? t('persons.compensations.form.addObligation')
            : t('persons.compensations.form.addEmployerCompensation');

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">{title}</h4>
                        <form id="compensationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-column width-100">
                                <div className="form-section width-90">
                                    <div className="form-group flex-column width-100" style={{ gap: '0.5rem' }}>
                                        <span className="span-bold">
                                            {t('persons.compensations.form.codeReasonKindRow')}
                                        </span>
                                        {optionsLoading ? (
                                            <p className="text-muted small mb-0">{t('common.loading')}</p>
                                        ) : (
                                            <select
                                                className="form-control"
                                                {...register('compensationCombo')}
                                                disabled={loading}
                                                autoComplete="off"
                                            >
                                                <option value="">{t('common.select')}</option>
                                                {dropdownOptions.map((o) => (
                                                    <option key={o.id} value={String(o.id)}>
                                                        {o.allData ?? ''}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {errors.compensationCombo && (
                                            <p className="error-message compact">
                                                {errors.compensationCombo.message}
                                            </p>
                                        )}
                                    </div>

                                    <div className="form-group flex-column width-100">
                                        <span className="span-bold">
                                            {t('persons.compensations.count')}
                                        </span>
                                        <input
                                            type="number"
                                            min={0}
                                            step="any"
                                            className="form-control"
                                            {...register('count')}
                                            disabled={loading}
                                        />
                                        {errors.count && (
                                            <p className="error-message compact">{errors.count.message}</p>
                                        )}
                                    </div>

                                    {variant === 'employer' && (
                                        <>
                                            <div className="form-group flex-column width-100">
                                                <span className="span-bold">
                                                    {t('persons.compensations.forYear')}
                                                </span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register('forYear')}
                                                    disabled={loading}
                                                />
                                                {errors.forYear && (
                                                    <p className="error-message compact">{errors.forYear.message}</p>
                                                )}
                                            </div>
                                            <div className="form-group flex-column width-100">
                                                <span className="span-bold">
                                                    {t('persons.compensations.sumToBeReceived')}
                                                </span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step="any"
                                                    className="form-control"
                                                    {...register('sumForDelivery')}
                                                    disabled={loading}
                                                />
                                                {errors.sumForDelivery && (
                                                    <p className="error-message compact">
                                                        {errors.sumForDelivery.message}
                                                    </p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <LoadingButton
                                    type="submit"
                                    className="btn btn-primary"
                                    loading={loading}
                                    disabled={loading || optionsLoading}
                                >
                                    {t('common.save')}
                                </LoadingButton>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
